/**
 * Compact account picker combobox for inline use in accounting previews.
 * Shows account name as clickable text, opens a searchable dropdown with "Create Account" option.
 *
 * Its read is unscoped by construction (no company prop), so under the aggregate
 * "All companies" scope it offers every accessible company's chart — where account
 * numbers repeat, so two rows read identically. It therefore carries the same
 * company qualifier the shared `AccountPicker` does, through the one gate both
 * read (`useCompanyDisambiguation`, which is where the reasoning lives). Here it
 * also has to reach the cmdk `value`, which is the string cmdk both filters and
 * SELECTS on: two options sharing one value are highlighted together.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import { useAccounts, useCreateAccount } from '../hooks/useAccounts';
import { LtrToken, BidiText } from '../../../shared/components/common';
// Imported from the MODULE, not the `common` barrel: adding an export to that
// barrel makes any branch touching it select most of the vitest corpus through
// the barrel graph, and this needs one constant.
import { POPOVER_WIDTH } from '../../../shared/components/common/EntityPickerBase';
import { RowCompanyIndicator, useCompanyDisambiguation } from '../../../shared/components/multi-company';
import { AccountForm } from './AccountForm';
import type { Account, AccountCreate, AccountUpdate } from '../types';

interface AccountPickerComboboxProps {
  accountId: string | null;
  accountName?: string;
  onAccountChange: (accountId: string, accountName: string) => void;
  /** Only show accounts matching these types (e.g., ['asset', 'liability']) */
  filterTypes?: string[];
  /** Exclude accounts matching these types (e.g., ['revenue', 'expense', 'direct_cost']) */
  excludeTypes?: string[];
}

export function AccountPickerCombobox({
  accountId,
  accountName,
  onAccountChange,
  filterTypes,
  excludeTypes,
}: AccountPickerComboboxProps) {
  const { t } = useTranslation('accounting');
  const [open, setOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data: accountsData } = useAccounts({ is_active: true });

  // Derived INSIDE the memo, off the stable `accountsData` reference — reading a
  // freshly-built `accounts` array as a dependency memoizes nothing.
  const accounts = useMemo<Account[]>(
    () => (accountsData && 'data' in accountsData ? (accountsData.data as Account[]) : []),
    [accountsData],
  );
  const visible = useMemo(
    () =>
      accounts
        .filter((a) => !a.is_header)
        .filter((a) => !filterTypes || filterTypes.includes(a.account_type))
        .filter((a) => !excludeTypes || !excludeTypes.includes(a.account_type)),
    [accounts, filterTypes, excludeTypes],
  );

  const companyOfAccount = useCompanyDisambiguation(visible);

  const createAccount = useCreateAccount();

  const handleSelect = (account: Account) => {
    onAccountChange(account.id, account.name);
    setOpen(false);
  };

  const handleCreateSubmit = async (data: AccountCreate | AccountUpdate) => {
    const result = await createAccount.mutateAsync(data as AccountCreate);
    const created = result && 'data' in result ? (result.data as Account) : null;
    if (created) {
      onAccountChange(created.id, created.name);
    }
    setShowCreateForm(false);
    setOpen(false);
  };

  const selected = accountId && accounts.length > 0
    ? accounts.find((a) => a.id === accountId)
    : undefined;
  const selectedCompany = selected ? companyOfAccount?.get(selected.company_id) : undefined;
  // Qualify the TRIGGER too, so the picked row is identifiable as the one that
  // was clicked. `accountName` is the caller's cached fallback for a row this
  // unscoped list has not loaded — it carries no company, so it stays bare.
  const displayName = selected
    ? (selectedCompany
        ? t('journalEntries.accountWithCompany', {
            account: selected.name,
            companyName: selectedCompany.name,
          })
        : selected.name)
    : accountName || '—';

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="link"
            className="h-auto p-0 truncate text-left text-foreground hover:text-primary"
            onClick={(e) => e.stopPropagation()}
          >
            {displayName}
          </Button>
        </PopoverTrigger>
        {/* Stop propagation so this picker works inside a DataTable `rowHref` row:
            Radix portals the dropdown out of the row's DOM, but React still bubbles
            the option click through the React tree to the row's navigate handler.
            Suppressing it here (the React parent of the options) keeps selecting an
            account from navigating the row away — and protects every consumer. */}
        {/* Shrink-to-fit the widest row, floored at the old 280px — the shared
            POPOVER_WIDTH, imported rather than re-typed so the two cannot drift.
            A fixed 280px truncated BOTH company names to near-identical stubs
            ("Atelier Leva…" / "Atelier Le…"), which is the very collision the
            qualifier exists to resolve, and wrapped the account name over three
            lines to do it. */}
        <PopoverContent
          className={cn(POPOVER_WIDTH, 'min-w-[280px]')}
          align="start"
          collisionPadding={8}
          onClick={(e) => e.stopPropagation()}
        >
          <Command>
            <CommandInput placeholder={t('journalEntries.searchAccount')} />
            <CommandList>
              <CommandEmpty>{t('journalEntries.noAccountFound')}</CommandEmpty>
              <CommandGroup>
                {visible.map((account) => {
                  const company = companyOfAccount?.get(account.company_id);
                  return (
                    <CommandItem
                      key={account.id}
                      value={[account.account_number, account.name, company?.name]
                        .filter(Boolean)
                        .join(' ')}
                      onSelect={() => handleSelect(account)}
                    >
                      <span className="shrink-0 text-muted-foreground mr-1.5"><LtrToken>{account.account_number}</LtrToken></span>
                      <span className="min-w-0 truncate"><BidiText>{account.name}</BidiText></span>
                      {companyOfAccount && (
                        <RowCompanyIndicator company={company} className="ms-auto ps-2" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setShowCreateForm(true);
                    setOpen(false);
                  }}
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  {t('documentImport.createNewAccount')}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {showCreateForm && (
        <AccountForm
          open={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateSubmit}
          isLoading={createAccount.isPending}
        />
      )}
    </>
  );
}
